// Angular core
import { Injectable, inject, signal, computed } from '@angular/core';
import { Router } from '@angular/router';

// RxJS
import { Observable, BehaviorSubject, throwError, firstValueFrom } from 'rxjs';

// Domain models
import {
  AuthSession,
  AuthConfig,
} from '../models/auth.models';

// Strategies
import { SimpleJwtTokenStrategy } from '../strategies/simple-jwt-token.strategy';

/**
 * Simplified Authentication Service for MVP
 * Only supports URL Token authentication
 */
@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private router = inject(Router);
  private jwtStrategy = inject(SimpleJwtTokenStrategy);

  // Configuration
  private config: AuthConfig = {
    strategy: 'simple-jwt',
    tokenStorage: "sessionStorage",
    autoRefresh: false,
    sessionTimeout: 1440, // minutes
  };

  // Session state management
  private sessionSubject = new BehaviorSubject<AuthSession | null>(null);
  public session$ = this.sessionSubject.asObservable();

  // Signals for reactive state
  private sessionSignal = signal<AuthSession | null>(null);

  // Computed properties
  public isAuthenticated = computed(() => {
    const session = this.sessionSignal();
    return session?.isAuthenticated ?? false;
  });

  public currentUser = computed(() => {
    const session = this.sessionSignal();
    return session?.user ?? null;
  });

  constructor() {
  }

  /**
   * Initialize authentication on app startup
   */
  private async initializeAuth(): Promise<void> {
  try {
    const storedSession = this.getStoredSession();
    if (storedSession && this.isSessionValid(storedSession)) {
      if (await this.validateStoredSession(storedSession)) {
        this.setSession(storedSession);
        return;
      } else {
        this.clearStoredSession();
      }
    }

    this.clearSession();
  } catch (error) {
    this.clearSession();
  }
}

  /**
   * Public method for APP_INITIALIZER
   */
  public async initSession(): Promise<void> {
    await this.initializeAuth();
  }

  /**
   * Validate stored session against current token validation rules
   */
  private async validateStoredSession(session: AuthSession): Promise<boolean> {
    if (!session.tokens?.accessToken) {
      return false;
    }

    try {
      // Use the JWT strategy to validate the stored token
      const isValid = await firstValueFrom(this.jwtStrategy.validateSession(session));
      return isValid;
    } catch (error) {
      return false;
    }
  }
  /**
   * Authenticate with JWT token
   */
  authenticateWithJwtToken(token: string): Observable<AuthSession | null> {
    if (!this.jwtStrategy.validateJwtToken) {
      return throwError(() => ({ code: 'NOT_SUPPORTED', message: 'Manual token validation not available' }));
    }

    return new Observable(observer => {
      this.jwtStrategy.validateJwtToken(token).subscribe({
        next: (session) => {
          if (session) {
            // Set session state and store it
            this.setSession(session);
            this.storeSession(session);
          }
          observer.next(session);
          observer.complete();
        },
        error: (error) => {
          observer.error(error);
        }
      });
    });
  }

  /**
   * Wait for authentication to complete (with timeout)
   */
  waitForAuthentication(timeoutMs: number = 5000): Promise<AuthSession | null> {
    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        reject(new Error('Authentication timeout'));
      }, timeoutMs);

      // Check if already authenticated
      if (this.isAuthenticated()) {
        clearTimeout(timeout);
        resolve(this.sessionSignal());
        return;
      }

      // Wait for authentication
      const subscription = this.session$.subscribe(session => {
        if (session?.isAuthenticated) {
          clearTimeout(timeout);
          subscription.unsubscribe();
          resolve(session);
        }
      });
    });
  }

  /**
   * Get current session synchronously
   */
  getCurrentSession(): AuthSession | null {
    return this.sessionSignal();
  }

  /**
   * Logout current user
   */
  logout(): Observable<void> {
    return new Observable(observer => {
      try {
        this.clearSession();
        observer.next();
        observer.complete();
      } catch (error) {
        observer.error(error);
      }
    });
  }

  /**
   * Set current session
   */
  private setSession(session: AuthSession): void {
    this.sessionSignal.set(session);
    this.sessionSubject.next(session);
  }

  /**
   * Clear current session
   */
  private clearSession(): void {
    this.clearStoredSession();
    this.sessionSignal.set(null);
    this.sessionSubject.next(null);
  }

  /**
   * Store session in configured storage
   */
  private storeSession(session: AuthSession): void {
    const storage = this.config.tokenStorage === 'sessionStorage' ? sessionStorage : localStorage;
    storage.setItem('auth_session', JSON.stringify(session));
  }

  /**
   * Get stored session from storage
   */
  private getStoredSession(): AuthSession | null {
    try {
      // First, try to get from the configured storage
      const storage = this.config.tokenStorage === 'sessionStorage' ? sessionStorage : localStorage;
      let stored = storage.getItem('auth_session');

      // If not found and we're using sessionStorage, check localStorage for migration
      if (!stored && this.config.tokenStorage === 'sessionStorage') {
        stored = localStorage.getItem('auth_session');
        if (stored) {
          // Migrate from localStorage to sessionStorage
          sessionStorage.setItem('auth_session', stored);
          localStorage.removeItem('auth_session');
        }
      }

      if (!stored) return null;

      const parsed = JSON.parse(stored);
      // Convert date strings back to Date objects
      parsed.sessionStarted = new Date(parsed.sessionStarted);
      parsed.lastActivity = new Date(parsed.lastActivity);

      return parsed;
    } catch {
      return null;
    }
  }

  /**
   * Clear stored session
   */
  private clearStoredSession(): void {
    localStorage.removeItem('auth_session');
    sessionStorage.removeItem('auth_session');
  }

  /**
   * Check if session is valid
   */
  private isSessionValid(session: AuthSession): boolean {
    const now = Date.now();
    const sessionAge = now - session.sessionStarted.getTime();
    const maxAge = this.config.sessionTimeout * 60 * 1000; // Convert to milliseconds

    return sessionAge < maxAge && session.isAuthenticated;
  }
}
